import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import {
  CoverageState,
  DrugsPrescriptionDetailsModel,
  GuaranteeDecision,
  PlanningAttemptView,
  PrescriptionLineView,
  V2PrescriptionPlanningModel,
  isAppointmentPlanning,
  isDrugsDetails,
  isDrugsPlanning,
  isLaboratoryPlanning,
} from '@lib/guarantees/data-access';
import { selectDiagnosesByGuaranteeId } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { ActorNamePipe } from '@lib/guarantees/feature/components/common/actor-name.pipe';
import { ChangeHistoryComponent } from '@lib/guarantees/feature/components/common/change-history.component';
import { ChipComponent } from '@lib/guarantees/feature/components/common/chip.component';
import { DiagnosisChipComponent } from '@lib/guarantees/feature/components/common/diagnosis-chip.component';
import { displayName } from '@lib/guarantees/feature/components/common/display-name';
import {
  COVERAGE_HINT_KEY,
  COVERAGE_ICON,
  COVERAGE_LABEL_KEY,
  COVERAGE_TONE,
  CREATOR_HINT_KEY,
  CREATOR_ICON,
  ChipTone,
  DECISION_AUTHORITY_HINT_KEY,
  DECISION_AUTO_ICON,
  DECISION_HINT_KEY,
  DECISION_ICON,
  DECISION_LABEL_KEY,
  DECISION_TONE,
  FULFILLMENT_TONE,
  MEAL_RELATION_LABEL_KEY,
  SERVICE_KIND_ICON,
  SERVICE_KIND_LABEL_KEY,
  TIME_UNIT_LABEL_KEY,
  fulfillmentIconFor,
  fulfillmentLabelKeyFor,
  syncStateIcon,
  syncStateLabelKey,
} from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import { PackageLimitsComponent } from '@lib/guarantees/feature/components/common/package-limits.component';
import { PanelSectionComponent } from '@lib/guarantees/feature/components/common/panel-section.component';
import { isAutoGuaranteed } from '@lib/guarantees/feature/components/common/prescription-decisions';
import { isPrescriptionUnmapped, prescribedAmount } from '@lib/guarantees/feature/components/common/prescription-mapping';
import { RecordBreachView, RecordBreachesComponent } from '@lib/guarantees/feature/components/common/record-breaches.component';
import { RecordedNoteComponent } from '@lib/guarantees/feature/components/common/recorded-note.component';
import { recordedNotesOf } from '@lib/guarantees/feature/components/common/recorded-notes';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CalloutComponent } from '../common/callout.component';

/** Flattened across the `_pt` subtypes so the template never narrows a union per field. */
interface PlanningRowView {
  planning: V2PrescriptionPlanningModel;
  targetGuaranteeId?: string;
  doctorName?: string;
  orderNumber?: string;
  prescriptionNumber?: string;
  meetingLink?: string;
}

/** Enough to see what kind of thing the line matched without the list becoming the panel. */
const TRADEMARK_PREVIEW_COUNT = 3;

function toPlanningRow(attempt: PlanningAttemptView): PlanningRowView {
  const { planning } = attempt;

  return {
    planning,
    targetGuaranteeId: attempt.targetGuaranteeId,
    doctorName: isAppointmentPlanning(planning) ? planning.doctorName : undefined,
    meetingLink: isAppointmentPlanning(planning) ? planning.meetingLink : undefined,
    orderNumber: isLaboratoryPlanning(planning) || isDrugsPlanning(planning) ? planning.orderNumber : undefined,
    prescriptionNumber: isDrugsPlanning(planning) ? planning.prescriptionNumber : undefined,
  };
}

/**
 * The whole of one prescription. Restate values the row already shows: a panel answering
 * "everything about this line" cannot have holes that send the reader back to the row.
 *
 * Facts read as horizontal runs rather than stacked label-over-value blocks: a prescription carries
 * two dozen of them, and stacked they become a column of one word per line with the width beside it
 * unused. Absent values are dropped rather than dashed — a run of placeholders reads as data.
 */
@Component({
  selector: 'guarantees-prescription-details',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    ActorNamePipe,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    RecordBreachesComponent,
    ChangeHistoryComponent,
    ChipComponent,
    DiagnosisChipComponent,
    PackageLimitsComponent,
    PanelSectionComponent,
    RecordedNoteComponent,
    CalloutComponent,
  ],
  templateUrl: './prescription-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionDetailsComponent {
  line = input.required<PrescriptionLineView>();

  /** A line that booked a later visit can jump to it; the page owns where that navigation goes. */
  guaranteeOpened = output<string>();

  private readonly store = inject(Store);
  private readonly diagnosesByGuarantee = this.store.selectSignal(selectDiagnosesByGuaranteeId);

  protected readonly prescription = computed(() => this.line().prescription);

  protected readonly kindIcon = computed(() => SERVICE_KIND_ICON[this.prescription().kind] ?? SERVICE_KIND_ICON.Unknown);
  protected readonly kindLabelKey = computed(() => SERVICE_KIND_LABEL_KEY[this.prescription().kind] ?? SERVICE_KIND_LABEL_KEY.Unknown);

  /** Stored but unusable — the same finding the row reports, restated where it can be understood. */
  protected readonly isUnmapped = computed(() => isPrescriptionUnmapped(this.prescription()));

  /** How much of the drug was prescribed, by both numbers that can state it. Absent on other kinds. */
  protected readonly amount = computed(() => prescribedAmount(this.prescription()));

  protected readonly decision = computed(() => this.prescription().decision?.decision ?? GuaranteeDecision.Pending);
  protected readonly decisionTone = computed(() => DECISION_TONE[this.decision()]);
  protected readonly decisionLabelKey = computed(() => DECISION_LABEL_KEY[this.decision()]);
  protected readonly decisionHintKey = computed(() => DECISION_HINT_KEY[this.decision()]);

  /** Marked on the chip as the row marks it; the panel then names the authority in full below. */
  protected readonly isAutoGuaranteed = computed(() => isAutoGuaranteed(this.prescription()));
  protected readonly decisionIcon = computed(() => (this.isAutoGuaranteed() ? DECISION_AUTO_ICON : DECISION_ICON[this.decision()]));
  protected readonly authorityHintKey = computed(() => {
    const authority = this.prescription().decision?.authority;

    return authority ? DECISION_AUTHORITY_HINT_KEY[authority] : '';
  });

  protected readonly coverage = computed(() => this.prescription().coverageEvaluation?.state ?? CoverageState.NotEvaluated);
  protected readonly coverageTone = computed(() => COVERAGE_TONE[this.coverage()]);
  protected readonly coverageIcon = computed(() => COVERAGE_ICON[this.coverage()]);
  protected readonly coverageLabelKey = computed(() => COVERAGE_LABEL_KEY[this.coverage()]);
  protected readonly coverageHintKey = computed(() => COVERAGE_HINT_KEY[this.coverage()]);

  /** The package options the line is drawn against — not shown once cover is refused, since nothing is drawn against them then. */
  protected readonly packageCoverage = computed(() => (this.decision() === GuaranteeDecision.NotGuaranteed ? undefined : this.prescription().coverage));

  protected readonly financials = computed(() => this.prescription().financials);

  protected readonly details = computed(() => this.prescription().details);
  protected readonly drugDetails = computed<DrugsPrescriptionDetailsModel | undefined>(() => {
    const details = this.details();

    return details && isDrugsDetails(details) ? details : undefined;
  });
  protected readonly schedule = computed(() => this.drugDetails()?.schedule);
  protected readonly drugs = computed(() => this.drugDetails()?.drugs ?? []);

  /** A common substance matches dozens, so state the count and list only the first few. */
  private readonly allTrademarksShown = signal(false);

  protected readonly visibleDrugs = computed(() => (this.allTrademarksShown() ? this.drugs() : this.drugs().slice(0, TRADEMARK_PREVIEW_COUNT)));

  protected readonly hiddenDrugCount = computed(() => Math.max(0, this.drugs().length - this.visibleDrugs().length));

  /** Only a list long enough to have been folded is worth offering to fold again. */
  protected readonly isTrademarkListFolded = computed(() => this.drugs().length > TRADEMARK_PREVIEW_COUNT);

  protected readonly scheduleMealKey = computed(() => {
    const relation = this.schedule()?.mealRelation;

    return relation ? MEAL_RELATION_LABEL_KEY[relation] : '';
  });

  /** The guarantee diagnosis the line is guaranteed against; unset leaves it unrulable. */
  protected readonly diagnosis = computed(() => {
    const diagnosisId = this.prescription().diagnosisId;

    return diagnosisId ? (this.diagnosesByGuarantee().get(this.prescription().guaranteeId) ?? []).find(entry => entry.id === diagnosisId) : undefined;
  });

  protected readonly syncLabelKey = syncStateLabelKey;
  protected readonly syncIcon = syncStateIcon;

  /** Position within a course, where the line belongs to one. A lone line has no ordinal to state. */
  protected readonly groupOrdinal = computed(() => this.prescription().grouping?.groupOrdinal);

  protected readonly categories = computed(() => (this.prescription().categories ?? []).map(entry => entry.categoryName).filter(Boolean));

  /** Newest attempt first: what happened last is what a manager is asked about. */
  protected readonly planningRows = computed(() => [...this.line().planningHistory].reverse().map(toPlanningRow));

  /**
   * The analyser's findings against this line. A line's own finding carries no severity of its
   * own — the record rolls one up across all of them — so every chip reads at that level.
   */
  protected readonly breaches = computed<RecordBreachView[]>(() => {
    const prescription = this.prescription();

    return (prescription.breaches ?? []).map((breach, index) => ({
      key: `${breach.code}:${breach.detectedAt}:${index}`,
      code: breach.code,
      detectedAt: breach.detectedAt,
      severity: prescription.maxBreachSeverity,
    }));
  });

  /** Only the described ones: a bare `type: value` pair is rule-engine internals, not a fact. */
  protected readonly conditions = computed(() => (this.prescription().conditions ?? []).filter(condition => !!condition.description?.trim()));

  /** Every reason the line carries — a refusal, an edit and a completion can be recorded at once. */
  protected readonly notes = computed(() => recordedNotesOf(this.prescription()));

  protected readonly wasUpdated = computed(() => this.prescription().updatedAt !== this.prescription().createdAt);

  protected readonly creatorIcon = computed(() => CREATOR_ICON[this.prescription().creatorType] ?? CREATOR_ICON.Unknown);
  protected readonly creatorHintKey = computed(() => CREATOR_HINT_KEY[this.prescription().creatorType] ?? CREATOR_HINT_KEY.Unknown);

  protected readonly createdByName = computed(() => displayName(this.prescription().createdByName));
  protected readonly updatedByName = computed(() => displayName(this.prescription().updatedByName));
  protected readonly deletedByName = computed(() => displayName(this.prescription().deletedByName));
  protected readonly decidedByName = computed(() => displayName(this.prescription().decision?.decidedByName));

  protected toggleTrademarks(): void {
    this.allTrademarksShown.update(shown => !shown);
  }

  protected timeUnitKey(unit?: string): string {
    return unit ? (TIME_UNIT_LABEL_KEY[unit as keyof typeof TIME_UNIT_LABEL_KEY] ?? '') : '';
  }

  protected attemptIcon(planning: V2PrescriptionPlanningModel): string {
    return fulfillmentIconFor(planning.fulfillmentState, planning.stopReason);
  }

  protected attemptLabelKey(planning: V2PrescriptionPlanningModel): string {
    return fulfillmentLabelKeyFor(planning.fulfillmentState, planning.stopReason);
  }

  protected attemptTone(planning: V2PrescriptionPlanningModel): ChipTone {
    return FULFILLMENT_TONE[planning.fulfillmentState] ?? FULFILLMENT_TONE.NotPlanned;
  }
}
